import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { RootState } from "../store";
import { BlogPost } from "../types/blog";
import { createBlogPost, fetchPosts } from "../store/slices/blogSlice";
import { useAppDispatch } from '../hooks/useAppDispatch';

const EditorContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background-color: var(--color-background-secondary);
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  color: var(--color-text-primary);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: var(--color-text-secondary);
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  background-color: var(--color-background-input);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  color: var(--color-text-primary);
  
  &::placeholder {
    color: var(--color-text-secondary);
  }

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  background-color: var(--color-background-input);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  color: var(--color-text-primary);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  background-color: var(--color-background-input);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  color: var(--color-text-primary);
  min-height: 200px;
  resize: vertical;
  
  &::placeholder {
    color: var(--color-text-secondary);
  }

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const TagButton = styled.button<{ selected: boolean }>`
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid var(--color-primary);
  background-color: ${props => props.selected ? 'var(--color-primary)' : 'transparent'};
  color: ${props => props.selected ? 'var(--color-text-inverse)' : 'var(--color-primary)'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props => props.selected ? 'var(--color-primary-dark)' : 'var(--color-primary-light)'};
    color: var(--color-text-inverse);
  }
`;

const FileInput = styled.div`
  position: relative;
  input[type="file"] {
    width: 100%;
    padding: 12px;
    font-size: 16px;
    background-color: var(--color-background-input);
    border: 1px solid var(--color-border);
    border-radius: 4px;
    color: var(--color-text-primary);
    cursor: pointer;
  }
`;

const PreviewImage = styled.img`
  max-width: 100%;
  height: auto;
  margin-top: 10px;
  border-radius: 4px;
`;

const SaveButton = styled.button`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  background-color: var(--color-primary);
  border: none;
  border-radius: 4px;
  color: var(--color-text-inverse);
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: var(--color-primary-dark);
  }

  &:disabled {
    background-color: var(--color-primary-light);
    cursor: not-allowed;
  }
`;

interface FormData {
  title: string;
  type: 'film' | 'serie';
  tags: string[];
  rating: number;
  year: string;
  duration: string;
  image: string;
  content: string;
  tmdbId?: number;
  poster?: string;
  overview?: string;
}

export const WriteBlog = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const mediaData = location.state?.mediaData;

  const [formData, setFormData] = useState<FormData>({
    title: "",
    type: "film",
    tags: [],
    rating: 5,
    year: new Date().getFullYear().toString(),
    duration: "",
    image: "",
    content: "",
    tmdbId: undefined,
    poster: "",
    overview: ""
  });

  useEffect(() => {
    if (mediaData) {
      setFormData(prev => ({
        ...prev,
        title: mediaData.title || "",
        type: mediaData.type === "movie" ? "film" : "serie",
        year: mediaData.release_date?.split("-")[0] || new Date().getFullYear().toString(),
        image: mediaData.backdrop_path ? `https://image.tmdb.org/t/p/original${mediaData.backdrop_path}` : "",
        poster: mediaData.poster_path ? `https://image.tmdb.org/t/p/w500${mediaData.poster_path}` : "",
        overview: mediaData.overview || "",
        tmdbId: mediaData.id
      }));
    }
  }, [mediaData]);

  if (!user || user.role !== "admin") {
    navigate("/");
    return null;
  }

  const availableTags = [
    "Action",
    "Aventure",
    "Animation",
    "Comédie",
    "Crime",
    "Documentaire",
    "Drame",
    "Familial",
    "Fantastique",
    "Guerre",
    "Histoire",
    "Horreur",
    "Musique",
    "Mystère",
    "Romance",
    "Science-Fiction",
    "Téléfilm",
    "Thriller",
    "Western"
  ];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    
    if (name === 'rating') {
      const numValue = parseFloat(value);
      setFormData(prev => ({ 
        ...prev, 
        [name]: isNaN(numValue) ? 5 : numValue 
      }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTagClick = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({ ...prev, image: reader.result as string }));
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const rating = parseFloat(formData.rating.toString());

      if (isNaN(rating)) {
        alert("La note doit être un nombre valide");
        return;
      }

      if (!formData.image) {
        alert("Une image est requise pour l'article");
        return;
      }

      if (!formData.title.trim()) {
        alert("Le titre est requis");
        return;
      }

      if (!formData.content.trim()) {
        alert("Le contenu de l'article est requis");
        return;
      }

      const post = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        type: formData.type,
        tmdbId: formData.tmdbId || 0,
        year: formData.year,
        image: formData.image,
        poster: formData.poster || "",
        overview: formData.overview || "",
        rating: rating,
        tags: formData.tags,
        authorId: user?.uid || '',
      };

      await dispatch(createBlogPost(post)).unwrap();
      await dispatch(fetchPosts()); // Recharger les articles
      navigate("/blog");
    } catch (error: any) {
      console.error("Erreur lors de la création de l'article:", error);
      alert(error.message || "Une erreur est survenue lors de la création de l'article");
    }
  };

  return (
    <EditorContainer>
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Titre du film/série</Label>
          <Input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Entrez le titre..."
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Type</Label>
          <Select name="type" value={formData.type} onChange={handleChange}>
            <option value="film">Film</option>
            <option value="serie">Série</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Catégories</Label>
          <TagsContainer>
            {availableTags.map(tag => (
              <TagButton
                key={tag}
                selected={formData.tags.includes(tag)}
                onClick={() => handleTagClick(tag)}
                type="button"
              >
                {tag}
              </TagButton>
            ))}
          </TagsContainer>
        </FormGroup>

        <FormGroup>
          <Label>Note sur 10</Label>
          <Input
            type="number"
            name="rating"
            value={formData.rating}
            onChange={handleChange}
            min="0"
            max="10"
            step="0.5"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Année</Label>
          <Input
            type="number"
            name="year"
            value={formData.year}
            onChange={handleChange}
            min="1900"
            max={new Date().getFullYear() + 1}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Durée (ex: 2h 30min)</Label>
          <Input
            type="text"
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            placeholder="2h 30min"
            required
          />
        </FormGroup>

        {!mediaData && (
          <FormGroup>
            <Label>Image de couverture</Label>
            <FileInput>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
            </FileInput>
          </FormGroup>
        )}

        {formData.image && (
          <FormGroup>
            <Label>Aperçu de l'image</Label>
            <PreviewImage src={formData.image} alt="Aperçu" />
          </FormGroup>
        )}

        <FormGroup>
          <Label>Contenu de l'article</Label>
          <TextArea
            name="content"
            value={formData.content}
            onChange={handleChange}
            placeholder="Écrivez votre article ici..."
            required
          />
        </FormGroup>

        <SaveButton type="submit">
          Publier l'article
        </SaveButton>
      </form>
    </EditorContainer>
  );
};

export default WriteBlog;
